import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { buildTheme, cssVariableResolver } from '~/assets'
import { PlayerProvider } from '~/components/player'
import { useAppStore } from '~/hooks/use-app-store'
import { AuthProvider, useAuth } from './Auth'
import { routeTree } from './routeTree.gen'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: 1,
    },
  },
})

const router = createRouter({
  routeTree,
  context: {
    session: {
      data: null,
      isPending: false,
      isRefetching: false,
      error: null,
      refetch: async () => {},
    },
    isAuthenticated: false,
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const auth = useAuth()
  return <RouterProvider router={router} context={auth} />
}

export default function App() {
  const themeId = useAppStore((s) => s.themeId)
  const theme = useMemo(() => buildTheme(themeId), [themeId])

  // Reads the resolved background off the document, so themeId is the trigger
  // that makes this re-run rather than a value the body references.
  // biome-ignore lint/correctness/useExhaustiveDependencies: themeId is the trigger, see above
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (!meta) return
    meta.content = getComputedStyle(document.body).backgroundColor
  }, [themeId])

  return (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={cssVariableResolver}
      defaultColorScheme="dark"
    >
      <Notifications position="top-right" />
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <PlayerProvider>
            <InnerApp />
          </PlayerProvider>
        </QueryClientProvider>
      </AuthProvider>
    </MantineProvider>
  )
}
